// Sign in. Jen uses Google. Support (or anyone without the spa's Google account) can use the password.

import { useEffect, useState } from 'preact/hooks';
import { api, errorText } from '../lib/api';
import { navigate, useLocation } from '../lib/router';
import { Button, Field, Notice, Spinner } from '../lib/ui';
import { GoogleLogo } from '../lib/icons';
import { Butterfly } from '../parts/Butterfly';

interface Setup {
  googleReady: boolean;
  connected: boolean;
  hasPassword: boolean;
}

const GOOGLE_MSG: Record<string, string> = {
  denied: 'Google sign-in was canceled. Tap Continue with Google to try again.',
  expired: 'That took a little too long. Tap Continue with Google to try again.',
  error: 'Google had a hiccup. Wait a minute, then tap Continue with Google again.',
  'no-keys': 'Google sign-in is not set up yet. Ask the person who set up this app to finish it.',
  'bad-keys': 'Google sign-in is not set up right yet. Ask the person who set up this app to check it.',
  'no-refresh': 'Google did not give the app permission to send. Tap Continue with Google and press Allow.',
  'missing-scope': 'The app needs permission to send email. Tap Continue with Google and leave every box checked.',
};

/** Retry errors get one more way out, so an error never ends with nothing to do. */
const STILL_STUCK = ' Still stuck? Close this page and open it again.';
const RETRY = new Set(['denied', 'expired', 'error', 'no-refresh', 'missing-scope', 'wrong-account']);

export function Login() {
  const { query } = useLocation();
  const [setup, setSetup] = useState<Setup | null>(null);
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [going, setGoing] = useState(false);

  useEffect(() => {
    api<Setup>('/api/setup')
      .then((s) => {
        setSetup(s);
        if (!s.googleReady && s.hasPassword) setUsePassword(true);
      })
      .catch((e) => setError(errorText(e)));
  }, []);

  const g = query.get('google');
  const googleMsg =
    g === 'wrong-account'
      ? `${query.get('account') || 'That account'} can't sign in here. Tap Continue with Google and choose the spa's account.`
      : g
        ? GOOGLE_MSG[g] || null
        : null;
  const googleNext = g && googleMsg && RETRY.has(g) ? STILL_STUCK : '';

  const google = () => {
    setGoing(true);
    location.href = setup?.connected ? '/api/google/start?mode=login' : '/api/google/start';
  };

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!password.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/login', { body: { password } });
      const next = query.get('next');
      navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/', { replace: true });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="auth">
      <div class="auth-card">
        <img class="auth-logo" src="/brand/logo.png" alt="LaVir Med Spa" />
        {!setup && !error ? (
          <Spinner />
        ) : (
          <>
            <h1 class="display">{setup?.connected ? 'Welcome back' : 'Welcome to LaVir Email'}</h1>
            <p class="lead">{setup?.connected ? 'Hi Jen. Sign in to see your emails.' : "Hi Jen. Sign in with the spa's Google account."}</p>
            {googleMsg && (
              <Notice kind="warn">
                {googleMsg}
                {googleNext}
              </Notice>
            )}
            {setup && (
              <button class="google-btn" onClick={google} disabled={going || !setup.googleReady}>
                {going ? <span class="spin" /> : <GoogleLogo size={20} />}
                <span>Continue with Google</span>
              </button>
            )}
            {usePassword ? (
              <form onSubmit={submit} class="auth-form">
                <Field label="Password" error={error}>
                  <input type="password" autocomplete="current-password" value={password} onInput={(e) => setPassword(e.currentTarget.value)} autoFocus={!setup?.googleReady} />
                </Field>
                <Button kind={setup?.googleReady ? 'secondary' : 'primary'} size="l" busy={busy} type="submit" class="wide">
                  Sign in
                </Button>
              </form>
            ) : (
              setup?.hasPassword && (
                <button class="text-link auth-alt" onClick={() => setUsePassword(true)}>
                  Use a password instead
                </button>
              )
            )}
            {setup && !setup.googleReady && <p class="muted small auth-note">Google sign-in turns on once setup is finished.</p>}
          </>
        )}
      </div>
      <p class="auth-made">
        <Butterfly size={18} dark open />
        <span>
          Hey<b>Spa</b>
        </span>
      </p>
    </div>
  );
}
